import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

/**
 * Plain multi-select filter — same trigger and list as the category filter, so
 * every filter on the page reads the same. Options are `{ value, label }` or
 * plain strings; the search box only appears once the list is long enough to
 * need it.
 */
export function KbTagFilter({
  options, value, onChange, allLabel = "All", className = "w-[150px]",
  testid = "kb-tag-filter",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => (options || []).map((o) => (
    typeof o === "string" ? { value: o, label: o } : o)), [options]);
  const picked = value || [];
  const chosen = new Set(picked);
  const label = picked.length === 0 ? allLabel
    : picked.length === 1 ? (rows.find((r) => r.value === picked[0])?.label || picked[0])
      : `${picked.length} selected`;

  const q = query.trim().toLowerCase();
  const opts = q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button type="button" title={label} data-testid={testid}
          className={`flex h-9 items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring ${className}`}>
          <span className={`truncate ${picked.length ? "" : "text-muted-foreground"}`}>{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" data-testid={`${testid}-options`}>
        {rows.length > 8 && (
          <div className="relative border-b border-slate-100 dark:border-slate-800 p-2">
            <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input autoFocus value={query} placeholder="Search…" className="h-8 pl-7 text-xs"
              onChange={(e) => setQuery(e.target.value)} data-testid={`${testid}-search`} />
          </div>
        )}
        <div className="max-h-72 overflow-y-auto p-1">
          <button type="button" onClick={() => onChange([])} data-testid={`${testid}-opt-all`}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${picked.length ? "text-slate-700 dark:text-slate-200" : "font-semibold text-primary"}`}>
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              {picked.length === 0 && <Check className="h-3.5 w-3.5" />}
            </span>
            {allLabel}
          </button>
          {opts.map((r) => {
            const on = chosen.has(r.value);
            return (
              <button key={r.value} type="button" data-testid={`${testid}-opt-${r.value}`}
                onClick={() => onChange(on ? picked.filter((p) => p !== r.value) : [...picked, r.value])}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800">
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-primary bg-primary text-primary-foreground" : "border-slate-300 dark:border-slate-600"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className={`truncate text-sm ${on ? "font-semibold text-primary" : "text-slate-700 dark:text-slate-200"}`}>
                  {r.label}
                </span>
              </button>
            );
          })}
          {opts.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-400 dark:text-slate-500">Nothing matches</div>
          )}
        </div>
        {picked.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 px-3 py-1.5 text-xs">
            <span className="text-slate-500 dark:text-slate-400">{picked.length} selected</span>
            <button type="button" onClick={() => onChange([])} data-testid={`${testid}-clear`}
              className="font-medium text-primary hover:underline">Clear</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
